// frontend/src/components/cohesion/KeyboardHelp.tsx
// Brighella v2 (ADR-013 cross-cutting #8) — Global keyboard shortcut overlay.
//
// `?` at any non-editable focus opens a dialog listing every shortcut the
// chrome surfaces. Escape closes. Mounted once at AppLayout root.
//
// Shortcuts here are STATIC — they represent the v2 chrome contract.
// Surface-specific shortcuts (Cmd+Enter to save inside a modal, etc.)
// should not migrate here; they belong to their own help affordance.

import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { useLanguage } from '../../i18n/index.jsx'
import './KeyboardHelp.css'

interface Shortcut {
  keys: string[]
  i18nKey: string
}

const SHORTCUTS: Shortcut[] = [
  { keys: ['⌘', 'K'], i18nKey: 'kbdShortcutCmdK' },
  { keys: ['⌘', 'Z'], i18nKey: 'kbdShortcutCmdZ' },
  { keys: ['?'],      i18nKey: 'kbdShortcutHelp' },
  { keys: ['Esc'],    i18nKey: 'kbdShortcutEsc' },
  { keys: ['Alt', 'hover'], i18nKey: 'kbdShortcutPeekHelp' },
]

export default function KeyboardHelp() {
  const [open, setOpen] = useState(false)
  const closeBtnRef = useRef<HTMLButtonElement>(null)
  const { t } = useLanguage()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        // UX-12 — this overlay is topmost when open: consume Escape so it
        // closes ONLY this overlay. Without this, the same Escape also hit
        // AppLayout's global handlers (closing the command palette under us
        // and re-dispatching a synthetic Escape at this [role="dialog"],
        // which recursed to a RangeError). Listener is registered in the
        // CAPTURE phase so we run before the document-level handlers;
        // stopImmediatePropagation also covers same-target dispatches.
        e.stopImmediatePropagation()
        e.stopPropagation()
        setOpen(false)
        return
      }
      if (e.key !== '?') return
      // Respect editable elements so `?` typed inside a form field works.
      const tgt = e.target as HTMLElement | null
      const tag = tgt?.tagName
      const editable = (
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
        tgt?.isContentEditable
      )
      if (editable) return
      // Some keyboard layouts emit `?` with Shift+/ — both fine.
      e.preventDefault()
      setOpen((prev) => !prev)
    }
    // Capture phase — must run before AppLayout's bubble-phase Esc handlers.
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open])

  useEffect(() => {
    if (open) closeBtnRef.current?.focus()
  }, [open])

  if (!open) return null

  return (
    <>
      <div className="kbd-help-scrim" aria-hidden="true" onClick={() => setOpen(false)} />
      <div
        className="kbd-help"
        role="dialog"
        aria-modal="true"
        aria-label={t('kbdHelpTitle')}
      >
        <header className="kbd-help-header">
          <span className="kbd-help-title">{t('kbdHelpTitle')}</span>
          <button
            ref={closeBtnRef}
            type="button"
            className="kbd-help-close"
            aria-label={t('close')}
            onClick={() => setOpen(false)}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        <ul className="kbd-help-list">
          {SHORTCUTS.map((s) => (
            <li key={s.i18nKey} className="kbd-help-row">
              <span className="kbd-help-keys">
                {s.keys.map((k, i) => (
                  <kbd key={i} className="kbd-help-key">{k}</kbd>
                ))}
              </span>
              <span className="kbd-help-label">{t(s.i18nKey)}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  )
}

export const _internals = { SHORTCUTS }
